import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Icon } from '@iconify/react';
import { useSubmitEpisodeEdit } from '../hooks/useMovie';
import { getApiErrorMessage } from '../api/errors';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter,
} from './ui/dialog';

const inputClass =
  'w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 outline-none transition-colors focus:border-primary/50 focus:bg-white/[0.05]';

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
        {label}
        {hint && <span className="ml-1 font-normal text-muted-foreground/60">{hint}</span>}
      </label>
      {children}
    </div>
  );
}

/**
 * A content creator's "edit this episode" form (title, description, episode/season number, and a
 * replacement video). For a plain Movie the feature video is its Episode 1, so this is also how a
 * movie's main video is replaced. Like RequestTitleEditModal, nothing goes live until an admin
 * approves, and only the fields that actually changed are sent.
 *
 * `episode` is a row from useMyMovies() (MyEpisodeDto).
 */
export function RequestEpisodeEditModal({ episode, onClose }) {
  const submitEdit = useSubmitEpisodeEdit();

  const initial = {
    title: episode.Title || '',
    description: episode.Description || '',
    episodeNumber: episode.EpisodeNumber ?? '',
    seasonNumber: episode.SeasonNumber ?? '',
  };

  const [form, setForm] = useState(initial);
  const [videoFile, setVideoFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const toInt = (v) => (v ? parseInt(v, 10) : '');

  const buildPayload = () => {
    const payload = {};
    if (form.title.trim() !== initial.title) payload.title = form.title.trim();
    if (form.description !== initial.description) payload.description = form.description;
    // Numbers can be changed but not blanked out (the API reads "missing" as "unchanged").
    if (form.episodeNumber !== '' && form.episodeNumber !== initial.episodeNumber) payload.episodeNumber = form.episodeNumber;
    if (form.seasonNumber !== '' && form.seasonNumber !== initial.seasonNumber) payload.seasonNumber = form.seasonNumber;
    if (videoFile) payload.videoFile = videoFile;
    return payload;
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) { toast.error('Title is required.'); return; }

    const payload = buildPayload();
    if (Object.keys(payload).length === 0) { toast.error('Change something first.'); return; }

    setUploadProgress(0);
    try {
      const { data } = await submitEdit.mutateAsync({
        episodeId: episode.Id,
        payload,
        onUploadProgress: (evt) => {
          if (evt.total) setUploadProgress(Math.round((evt.loaded * 100) / evt.total));
        },
      });
      if (data.success) {
        toast.success(data.message || 'Edit submitted for review.');
        onClose();
      } else {
        toast.error(data.message || 'Could not submit that edit.');
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not submit that edit.'));
    } finally {
      setUploadProgress(0);
    }
  };

  return (
    <Dialog open onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon icon="ph:pencil-simple-bold" />
            Request episode edit
          </DialogTitle>
        </DialogHeader>

        <DialogBody className="gap-5">
          <div className="flex items-start gap-2.5 rounded-lg border border-primary/25 bg-primary/[0.07] px-3.5 py-3 text-xs text-primary">
            <Icon icon="ph:info-bold" className="mt-0.5 size-4 shrink-0" />
            <span>
              Your changes are reviewed by an admin before they go live. Until then this episode
              stays exactly as it is.
            </span>
          </div>

          <Field label="Title">
            <input className={inputClass} value={form.title} onChange={(e) => set({ title: e.target.value })} />
          </Field>

          <Field label="Description">
            <textarea
              className={`${inputClass} resize-none`}
              rows={3}
              value={form.description}
              onChange={(e) => set({ description: e.target.value })}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Episode #">
              <input
                type="number" min="1" className={inputClass} value={form.episodeNumber}
                onChange={(e) => set({ episodeNumber: toInt(e.target.value) })}
              />
            </Field>
            <Field label="Season #" hint="(optional)">
              <input
                type="number" min="1" className={inputClass} value={form.seasonNumber}
                placeholder="(none)"
                onChange={(e) => set({ seasonNumber: toInt(e.target.value) })}
              />
            </Field>
          </div>

          <Field label="Replace video" hint="(leave blank to keep current)">
            <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-white/15 bg-white/[0.02] px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:bg-white/[0.04]">
              <Icon icon="ph:video-camera-bold" className="size-4 shrink-0" />
              <span className="truncate">{videoFile ? videoFile.name : 'Choose video file…'}</span>
              <input type="file" accept="video/*" onChange={(e) => setVideoFile(e.target.files?.[0] || null)} className="hidden" />
            </label>
            {videoFile && (
              <p className="mt-1.5 text-xs text-muted-foreground">
                The current video is only replaced if an admin approves this edit.
              </p>
            )}
          </Field>

          {submitEdit.isPending && (
            <div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
                <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${uploadProgress}%` }} />
              </div>
              <p className="mt-1.5 text-xs text-muted-foreground">
                {uploadProgress < 100 ? `Uploading… ${uploadProgress}%` : 'Finishing up…'}
              </p>
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={onClose} className="gap-1.5">
            <Icon icon="ph:x-bold" />
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={submitEdit.isPending} className="gap-1.5">
            <Icon icon="ph:paper-plane-tilt-bold" />
            {submitEdit.isPending ? 'Submitting…' : 'Submit for review'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default RequestEpisodeEditModal;
